<template>
  <div ref="messages" class="chat-messages d-flex flex-column flex-grow-1 p-3">
    <ChatIconText v-if="!messages.length">
      No messages here yet, why not send one?
    </ChatIconText>

    <template v-for="(message, i) in messages">
      <div
        v-if="isGap(i)"
        :key="`${message.id}-timestamp`"
        class="small text-muted text-center my-2"
      >
        {{ timeString(message.created_at, true) }}
      </div>

      <div
        v-if="(!isAdjSameUser(i, -1, true) || isGap(i)) && !isMine(message)"
        :key="`${message.id}-username`"
        class="mt-2 ml-2 text-muted"
      >
        {{ getNickname(message.user) }}
      </div>

      <div
        :key="`${message.id}-message`"
        class="message-line d-flex"
        :class="{
          'justify-content-end': isMine(message),
          'justify-content-start': !isMine(message)
        }"
      >
        <b-btn
          v-if="isMine(message) && message.message !== null"
          class="message-delete"
          variant="link"
          @click="deleteMessage(message.id)"
        >
          <i class="icon-trash text-danger" />
        </b-btn>

        <span
          v-if="isOnlyEmoji(message.message)"
          v-b-tooltip.ds500.left.hover="timeString(message.created_at)"
          class="font-6xl"
          >{{ message.message }}</span
        >

        <span
          v-else-if="message.message !== ''"
          v-b-tooltip.ds500.left.hover="timeString(message.created_at)"
          class="message"
          :class="{
            mine: isMine(message),
            'bg-primary': isMine(message) && message.message,
            'bg-white': !isMine(message) && message.message,
            'bg-secondary': !message.message,
            'same-above': isAdjSameUser(i, -1) && !isGap(i),
            'same-below': isAdjSameUser(i, 1) && !isGap(i + 1)
          }"
          >{{ message.message || "message deleted" }}</span
        >

        <template v-else-if="message.attachment">
          <ChatAttachmentImage
            v-if="/\.(gif|jpe?g|png)$/.test(message.attachment.filename)"
            v-bind="message.attachment"
            :attachment="message.attachment"
            class="my-1"
            @load="scrollImage"
          />

          <ChatAttachment
            v-else
            v-bind="message.attachment"
            class="bg-white my-1"
            downloadable
            hide-remove
          />
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters, mapState } from "vuex";
import API from "@/api";

import ChatAttachment from "./ChatAttachment";
import ChatAttachmentImage from "./ChatAttachmentImage";
import ChatIconText from "./ChatIconText";

export default {
  components: {
    ChatAttachment,
    ChatAttachmentImage,
    ChatIconText
  },

  computed: {
    ...mapGetters("contacts", ["getNickname"]),
    ...mapState("chat", ["messages", "selectedId", "totalMessages"]),
    ...mapState(["user"])
  },

  watch: {
    totalMessages(val, oldVal) {
      if (val > oldVal) {
        this.scrollToLatest();
      }
    }
  },

  mounted() {
    this.$refs.messages.addEventListener("scroll", this.onScroll);
  },

  methods: {
    ...mapActions("chat", ["getMessages", "updateLastRead"]),

    async deleteMessage(id) {
      const confirm = await this.$bvModal.msgBoxConfirm(
        "Are you sure you want to delete this message? If deleted after 30s, message will show in chat as deleted.",
        {
          title: "Delete Message",
          okTitle: "Yes",
          okVariant: "danger",
          size: "sm",
          centered: true
        }
      );
      if (!confirm) return;

      API.deleteChatMessage(id);
    },

    isAdjSameUser(i, dir, ignoreEmoji = false) {
      if (i + dir < 0 || i + dir >= this.messages.length) return false;
      const adj = this.messages[i + dir];
      if (!ignoreEmoji && this.isOnlyEmoji(adj.message)) return false;
      return adj.user.id === this.messages[i].user.id;
    },

    isOnlyEmoji(message) {
      return /^\p{Extended_Pictographic}{1,3}$/u.test(message);
    },

    isGap(i) {
      if (i === 0) return true;
      const curr_at = this.messages[i].created_at;
      const last_at = this.messages[i - 1].created_at;
      return moment(curr_at).diff(moment(last_at), "minutes") >= 30;
    },

    isMine(message) {
      return message.user.id === this.user.id;
    },

    async onScroll() {
      const { clientHeight, scrollHeight, scrollTop } = this.$refs.messages;
      if (scrollTop > clientHeight / 2) return;

      const loaded = await this.getMessages();
      if (!loaded) return;
      await this.$nextTick();

      const { scrollHeight: newScrollHeight } = this.$refs.messages;
      const diffHeight = newScrollHeight - scrollHeight;
      this.$refs.messages.scrollTop = diffHeight + scrollTop;
    },

    async scrollImage(el) {
      if (el.offsetTop + el.offsetHeight > this.$refs.messages.scrollTop) {
        this.$refs.messages.scrollTop += el.offsetHeight;
      }
    },

    async scrollToLatest() {
      API.updateChatLastRead(this.selectedId);
      this.updateLastRead();

      await this.$nextTick();
      const messages = this.$refs.messages;
      messages.scrollTop = messages.scrollHeight;
    },

    timeString(date, hideSeconds = false) {
      return new Date(date).toLocaleString(navigator.language, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "numeric",
        second: hideSeconds ? undefined : "numeric"
      });
    }
  }
};
</script>

<style lang="scss">
.chat-messages {
  height: 0;
  overflow-y: auto;

  .message-line {
    .message-delete {
      opacity: 0;
      transition: opacity 0.2s;
    }

    &:hover {
      .message-delete {
        opacity: 1;
      }
    }
  }

  .message {
    max-width: min(80%, 40em);
    padding: 0.5rem 1rem;
    border-radius: 1.5em;
    word-break: break-word;
    white-space: pre-wrap;

    &.same-below {
      margin-bottom: 2px;
    }

    &.same-above.mine {
      border-top-right-radius: 4px !important;
    }

    &.same-below.mine {
      border-bottom-right-radius: 4px !important;
    }

    &.same-above:not(.mine) {
      border-top-left-radius: 4px !important;
    }

    &.same-below:not(.mine) {
      border-bottom-left-radius: 4px !important;
    }
  }
}
</style>
